import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import TankAvatar from '@/components/TankAvatar';
import {
  ChevronLeft,
  ChevronRight,
  Shield,
  Radio,
  Package,
  Cloud,
  Target,
  AlertTriangle,
  Activity,
  Zap,
  Eye,
  Wifi,
  WifiOff,
  MapPin,
  Clock,
  TrendingUp,
  TrendingDown,
  Users,
  Truck,
  Heart,
  Thermometer,
  Wind,
  Droplets,
  Skull,
  Camera,
  MessageSquare,
  Send,
  Navigation,
  Warehouse,
  GraduationCap,
  ClipboardCheck,
  Play,
  RefreshCw,
  Home,
  ChevronDown,
  Headphones,
  Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category configuration
const CATEGORIES = {
  tactical: {
    label: 'Tactical',
    icon: Target,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
    subcategories: ['predictions', 'osint', 'grid', 'supply']
  },
  comms: {
    label: 'Communications',
    icon: Radio,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    subcategories: ['radio', 'group', 'deaddrops']
  },
  logistics: {
    label: 'Logistics',
    icon: Package,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    subcategories: ['evacuation', 'rally', 'inventory']
  },
  environmental: {
    label: 'Environmental',
    icon: Cloud,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/30',
    subcategories: ['disease', 'weather', 'airquality']
  },
  training: {
    label: 'Training',
    icon: GraduationCap,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    subcategories: ['scenarios', 'readiness', 'members']
  }
};

// Breadcrumb component
const Breadcrumb = ({ items, onNavigate }) => (
  <div className="flex items-center gap-2 text-sm mb-4">
    <button 
      onClick={() => onNavigate('home')}
      className="flex items-center gap-1 text-slate-400 hover:text-white transition-colors"
    >
      <Home className="w-4 h-4" />
      <span>Ops Center</span>
    </button>
    {items.map((item, idx) => (
      <React.Fragment key={idx}>
        <ChevronRight className="w-4 h-4 text-slate-600" />
        <button 
          onClick={() => onNavigate(item.key)}
          className={`${idx === items.length - 1 ? 'text-teal-400' : 'text-slate-400 hover:text-white'} transition-colors`}
        >
          {item.label}
        </button>
      </React.Fragment>
    ))}
  </div>
);

// Stat card component
const StatCard = ({ icon: Icon, label, value, subValue, color, trend }) => (
  <div className="glass-card p-4 rounded-lg border border-slate-800/50">
    <div className="flex items-center justify-between mb-2">
      <div className={`p-2 rounded-lg ${color.replace('text-', 'bg-')}/10`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      {trend && (
        <div className={`flex items-center gap-1 text-xs ${trend > 0 ? 'text-red-400' : 'text-green-400'}`}>
          {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    <div className="text-2xl font-bold text-white font-mono">{value}</div>
    <div className="text-xs text-slate-500 mt-1">{label}</div>
    {subValue && <div className="text-xs text-slate-400 mt-1">{subValue}</div>}
  </div>
);

// Main Ops Center Component
const OpsCenter = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Navigation state
  const [activeCategory, setActiveCategory] = useState(searchParams.get('cat') || null);
  const [activeSubcategory, setActiveSubcategory] = useState(searchParams.get('sub') || null);
  
  // Data state
  const [predictions, setPredictions] = useState([]);
  const [osint, setOsint] = useState([]);
  const [gridStatus, setGridStatus] = useState(null);
  const [supplyChain, setSupplyChain] = useState(null);
  const [radioFeeds, setRadioFeeds] = useState([]);
  const [radioTranscripts, setRadioTranscripts] = useState([]);
  const [groupMessages, setGroupMessages] = useState([]);
  const [deadDrops, setDeadDrops] = useState([]);
  const [evacuationRoutes, setEvacuationRoutes] = useState([]);
  const [rallyPoints, setRallyPoints] = useState([]);
  const [inventory, setInventory] = useState(null);
  const [diseaseData, setDiseaseData] = useState(null);
  const [weatherForecast, setWeatherForecast] = useState(null);
  const [airQuality, setAirQuality] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [members, setMembers] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [selectedRadioFeed, setSelectedRadioFeed] = useState(null);
  
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Fetch functions
  const fetchPredictions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tactical/predictions`, { headers });
      setPredictions(res.data.predictions || []);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchOsint = useCallback(async () => {
    try {
      // Fetch from real OSINT aggregated feed
      const res = await axios.get(`${API_URL}/api/osint/feed?limit=100`, { headers });
      setOsint(res.data.items || []);
    } catch (e) { 
      console.error('OSINT fetch error:', e);
      // Fallback to mock data if real API fails
      try {
        const fallback = await axios.get(`${API_URL}/api/tactical/osint`, { headers });
        setOsint(fallback.data.alerts || []);
      } catch (e2) { console.error(e2); }
    }
  }, [headers]);

  const fetchGridStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tactical/grid`, { headers });
      setGridStatus(res.data);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchSupplyChain = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tactical/supply-chain`, { headers });
      setSupplyChain(res.data);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchRadioFeeds = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/comms/radio/feeds`, { headers });
      setRadioFeeds(res.data.feeds || []);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchRadioTranscripts = useCallback(async (feedId) => {
    try {
      const res = await axios.get(`${API_URL}/api/comms/radio/transcripts/${feedId}`, { headers });
      setRadioTranscripts(res.data.transcripts || []);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchGroupMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/comms/group/messages`, { headers });
      setGroupMessages(res.data.messages || []);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchDeadDrops = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/comms/deaddrops`, { headers });
      setDeadDrops(res.data.drops || []);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchEvacuationRoutes = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/logistics/evacuation/routes`, { headers });
      setEvacuationRoutes(res.data.routes || []);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchRallyPoints = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/logistics/rally-points`, { headers });
      setRallyPoints(res.data.rally_points || []);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchInventory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/logistics/inventory`, { headers });
      setInventory(res.data);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchDiseaseData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/environmental/disease/outbreaks`, { headers });
      setDiseaseData(res.data);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchWeatherForecast = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/environmental/weather/forecast`, { headers });
      setWeatherForecast(res.data);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchAirQuality = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/environmental/air-quality`, { headers });
      setAirQuality(res.data);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchScenarios = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/training/scenarios`, { headers });
      setScenarios(res.data.scenarios || []);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchReadiness = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/training/readiness`, { headers });
      setReadiness(res.data);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/training/members`, { headers });
      setMembers(res.data.members || []);
    } catch (e) { console.error(e); }
  }, [headers]);

  // Load data based on active view
  useEffect(() => {
    if (!token) return;
    
    setLoading(true);
    
    const loadData = async () => {
      if (!activeCategory) {
        // Load overview data
        await Promise.all([
          fetchPredictions(),
          fetchGridStatus(),
          fetchReadiness(),
          fetchOsint()
        ]);
      } else if (activeCategory === 'tactical') {
        await Promise.all([fetchPredictions(), fetchOsint(), fetchGridStatus(), fetchSupplyChain()]);
      } else if (activeCategory === 'comms') {
        await Promise.all([fetchRadioFeeds(), fetchGroupMessages(), fetchDeadDrops()]);
      } else if (activeCategory === 'logistics') {
        await Promise.all([fetchEvacuationRoutes(), fetchRallyPoints(), fetchInventory()]);
      } else if (activeCategory === 'environmental') {
        await Promise.all([fetchDiseaseData(), fetchWeatherForecast(), fetchAirQuality()]);
      } else if (activeCategory === 'training') {
        await Promise.all([fetchScenarios(), fetchReadiness(), fetchMembers()]);
      }
      setLoading(false);
    };
    
    loadData();
  }, [token, activeCategory]);

  // Handle navigation
  const handleNavigate = (key) => {
    if (key === 'home') {
      setActiveCategory(null);
      setActiveSubcategory(null);
      setSearchParams({});
    } else if (Object.keys(CATEGORIES).includes(key)) {
      setActiveCategory(key);
      setActiveSubcategory(null);
      setSearchParams({ cat: key });
    } else {
      setActiveSubcategory(key);
      setSearchParams({ cat: activeCategory, sub: key });
    }
  };

  // Build breadcrumb items
  const breadcrumbItems = [];
  if (activeCategory) {
    breadcrumbItems.push({ key: activeCategory, label: CATEGORIES[activeCategory].label });
  }
  if (activeSubcategory) {
    const subLabels = {
      predictions: 'Threat Predictions',
      osint: 'OSINT Feed',
      grid: 'Grid Status',
      supply: 'Supply Chain',
      radio: 'Radio Transcription',
      group: 'Group Comms',
      deaddrops: 'Dead Drops',
      evacuation: 'Evacuation Routes',
      rally: 'Rally Points',
      inventory: 'Inventory',
      disease: 'Disease Tracking',
      weather: 'Weather Forecast',
      airquality: 'Air Quality',
      scenarios: 'Training Scenarios',
      readiness: 'Readiness Score',
      members: 'Team Members'
    };
    breadcrumbItems.push({ key: activeSubcategory, label: subLabels[activeSubcategory] || activeSubcategory });
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl text-white mb-2">Authentication Required</h2>
          <p className="text-slate-400 mb-4">Please sign in to access the Ops Center</p>
          <Button onClick={() => navigate('/auth')} className="bg-teal-600 hover:bg-teal-500">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  // Render Overview (Home)
  const renderOverview = () => (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          icon={AlertTriangle} 
          label="Active Threats" 
          value={predictions.length}
          color="text-red-500"
        />
        <StatCard 
          icon={Eye} 
          label="OSINT Alerts" 
          value={osint.length}
          color="text-amber-500"
        />
        <StatCard 
          icon={Activity} 
          label="Grid Status" 
          value={gridStatus?.overall_status || 'Loading...'}
          color="text-cyan-500"
        />
        <StatCard 
          icon={Target} 
          label="Readiness" 
          value={readiness?.overall_score ? `${readiness.overall_score}%` : '...'}
          color="text-purple-500"
        />
      </div>

      {/* Category Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(CATEGORIES).map(([key, cat]) => {
          const Icon = cat.icon;
          return (
            <button
              key={key}
              onClick={() => handleNavigate(key)}
              className={`glass-card p-6 rounded-xl border ${cat.borderColor} hover:border-opacity-100 transition-all text-left group`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-xl ${cat.bgColor}`}>
                  <Icon className={`w-6 h-6 ${cat.color}`} />
                </div>
                <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-slate-400 transition-colors" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1">{cat.label}</h3>
              <p className="text-sm text-slate-400">
                {key === 'tactical' && 'Threat predictions, OSINT, grid monitoring'}
                {key === 'comms' && 'Radio transcription, secure messaging'}
                {key === 'logistics' && 'Routes, rally points, inventory'}
                {key === 'environmental' && 'Disease, weather, air quality'}
                {key === 'training' && 'Scenarios, readiness, team status'}
              </p>
            </button>
          );
        })}
      </div>

      {/* Recent Alerts */}
      <div className="glass-card rounded-xl border border-slate-800 p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-500" />
          Recent Alerts
        </h3>
        <div className="space-y-3">
          {osint.slice(0, 5).map((alert) => {
            const level = alert.severity || alert.threat_level || 'low';
            const isHighSeverity = level === 'high' || level === 'critical';
            const isMediumSeverity = level === 'medium';
            
            return (
              <div key={alert.id} className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-800">
                <div className={`w-2 h-2 rounded-full mt-2 ${
                  isHighSeverity ? 'bg-red-500' :
                  isMediumSeverity ? 'bg-amber-500' : 'bg-blue-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{alert.title}</p>
                  <p className="text-xs text-slate-500">{alert.source_name || alert.source} • {new Date(alert.timestamp).toLocaleTimeString()}</p>
                </div>
                <Badge className={`text-xs ${
                  isHighSeverity ? 'bg-red-500/20 text-red-400' :
                  isMediumSeverity ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
                }`}>
                  {level}
                </Badge>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Render Tactical Category
  const renderTactical = () => (
    <div className="space-y-6">
      <Tabs defaultValue={activeSubcategory || 'predictions'} onValueChange={(v) => handleNavigate(v)}>
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="predictions">Threat Predictions</TabsTrigger>
          <TabsTrigger value="osint">OSINT Feed</TabsTrigger>
          <TabsTrigger value="grid">Grid Status</TabsTrigger>
          <TabsTrigger value="supply">Supply Chain</TabsTrigger>
        </TabsList>

        <TabsContent value="predictions" className="mt-4">
          <div className="space-y-4">
            {/* Tank's AI Analysis Header */}
            <div className="glass-card p-4 rounded-lg border border-teal-500/30 bg-gradient-to-r from-slate-900 to-slate-800">
              <div className="flex items-center gap-4">
                <TankAvatar size="lg" showTooltip={true} />
                <div>
                  <h3 className="font-bold text-teal-400 text-lg">Tank's Threat Analysis</h3>
                  <p className="text-sm text-slate-400">AI-powered predictions based on current intel and historical patterns</p>
                </div>
              </div>
            </div>
            
            {predictions.map((pred) => (
              <div key={pred.id} className="glass-card p-4 rounded-lg border border-slate-800">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-bold text-white">{pred.threat_name}</h4>
                    <p className="text-sm text-slate-400">{pred.location?.area}</p>
                  </div>
                  <Badge className={`${
                    pred.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                    pred.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                    pred.severity === 'medium' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {Math.round(pred.probability * 100)}% probability
                  </Badge>
                </div>
                <div className="mb-3">
                  <div className="text-xs text-slate-500 mb-1">Confidence</div>
                  <Progress value={pred.confidence * 100} className="h-2" />
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {pred.factors?.map((factor, i) => (
                    <span key={i} className="text-xs px-2 py-1 rounded bg-slate-800 text-slate-300">{factor}</span>
                  ))}
                </div>
                <p className="text-sm text-slate-400">{pred.recommendation}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="osint" className="mt-4">
          <ScrollArea className="h-[600px]">
            <div className="space-y-3">
              {osint.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Loading OSINT feeds...</p>
                </div>
              ) : (
                osint.map((alert) => {
                  // Handle both new (severity) and old (threat_level) data formats
                  const level = alert.severity || alert.threat_level || 'low';
                  const isHighSeverity = level === 'high' || level === 'critical';
                  const isMediumSeverity = level === 'medium';
                  
                  return (
                    <div key={alert.id} className="glass-card p-4 rounded-lg border border-slate-800 hover:border-slate-700 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          isHighSeverity ? 'bg-red-500/20' :
                          isMediumSeverity ? 'bg-amber-500/20' : 'bg-blue-500/20'
                        }`}>
                          <Eye className={`w-4 h-4 ${
                            isHighSeverity ? 'text-red-400' :
                            isMediumSeverity ? 'text-amber-400' : 'text-blue-400'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-xs text-slate-500">{alert.source_name || alert.source}</span>
                            <Badge className={`text-xs ${
                              alert.category === 'earthquake' ? 'bg-orange-500/20 text-orange-400' :
                              alert.category === 'weather' ? 'bg-cyan-500/20 text-cyan-400' :
                              alert.category === 'law_enforcement' ? 'bg-red-500/20 text-red-400' :
                              alert.category === 'military' ? 'bg-purple-500/20 text-purple-400' :
                              'bg-slate-500/20 text-slate-400'
                            }`}>{alert.category || 'general'}</Badge>
                            {alert.verified && <Badge className="bg-green-500/20 text-green-400 text-xs">Verified</Badge>}
                          </div>
                          <p className="text-white font-medium">{alert.title}</p>
                          {alert.summary && <p className="text-sm text-slate-400 mt-1 line-clamp-2">{alert.summary}</p>}
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
                            <span>{new Date(alert.timestamp).toLocaleString()}</span>
                            {alert.engagement && <span>{alert.engagement} engagements</span>}
                            {alert.magnitude && <span>Magnitude: {alert.magnitude}</span>}
                            {alert.location?.place && <span>{alert.location.place}</span>}
                          </div>
                          {alert.url && (
                            <a 
                              href={alert.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-teal-400 hover:text-teal-300 mt-2 inline-block"
                            >
                              View Source
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="grid" className="mt-4">
          {gridStatus && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Zap} label="Power Systems" value={gridStatus.infrastructure?.filter(i => i.infrastructure_type === 'power_grid' && i.status === 'operational').length || 0} color="text-yellow-500" />
                <StatCard icon={Droplets} label="Water Systems" value={gridStatus.infrastructure?.filter(i => i.infrastructure_type === 'water' && i.status === 'operational').length || 0} color="text-blue-500" />
                <StatCard icon={Wifi} label="Telecom Online" value={gridStatus.infrastructure?.filter(i => i.infrastructure_type === 'telecom' && i.status === 'operational').length || 0} color="text-green-500" />
                <StatCard icon={Activity} label="Overall Status" value={gridStatus.overall_status} color="text-teal-500" />
              </div>
              <div className="space-y-3">
                {gridStatus.infrastructure?.map((infra) => (
                  <div key={infra.id} className="glass-card p-4 rounded-lg border border-slate-800 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${
                        infra.status === 'operational' ? 'bg-green-500' :
                        infra.status === 'degraded' ? 'bg-amber-500' : 'bg-red-500'
                      }`} />
                      <div>
                        <p className="text-white font-medium">{infra.name}</p>
                        <p className="text-xs text-slate-500">{infra.operator}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-300">{Math.round(infra.capacity * 100)}% capacity</p>
                      <p className="text-xs text-slate-500">{infra.trend}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="supply" className="mt-4">
          {supplyChain && (
            <div className="space-y-4">
              {supplyChain.critical_alerts > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <p className="text-red-400 font-medium">{supplyChain.critical_alerts} critical supply alerts</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {supplyChain.items?.slice(0, 20).map((item) => (
                  <div key={item.id} className="glass-card p-4 rounded-lg border border-slate-800">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">{item.item}</span>
                      <Badge className={`${
                        item.status === 'in_stock' ? 'bg-green-500/20 text-green-400' :
                        item.status === 'low_stock' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {item.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">{item.category}</span>
                      <span className={`${item.price_change_percent > 5 ? 'text-red-400' : item.price_change_percent < -2 ? 'text-green-400' : 'text-slate-400'}`}>
                        {item.price_change_percent > 0 ? '+' : ''}{item.price_change_percent}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );

  // Render Communications Category
  const renderComms = () => (
    <div className="space-y-6">
      <Tabs defaultValue={activeSubcategory || 'radio'} onValueChange={(v) => handleNavigate(v)}>
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="radio">Radio Transcription</TabsTrigger>
          <TabsTrigger value="group">Group Comms</TabsTrigger>
          <TabsTrigger value="deaddrops">Dead Drops</TabsTrigger>
        </TabsList>

        <TabsContent value="radio" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1">
              <h4 className="text-sm font-medium text-slate-400 mb-3">Available Feeds</h4>
              <div className="space-y-2">
                {radioFeeds.map((feed) => (
                  <button
                    key={feed.id}
                    onClick={() => {
                      setSelectedRadioFeed(feed);
                      fetchRadioTranscripts(feed.id);
                    }}
                    className={`w-full glass-card p-3 rounded-lg border transition-all text-left ${
                      selectedRadioFeed?.id === feed.id ? 'border-teal-500 bg-teal-500/10' : 'border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Headphones className="w-4 h-4 text-teal-400" />
                        <span className="text-sm text-white">{feed.name}</span>
                      </div>
                      <div className={`w-2 h-2 rounded-full ${feed.status === 'active' ? 'bg-green-500' : 'bg-slate-500'}`} />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{feed.frequency}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="lg:col-span-2">
              <h4 className="text-sm font-medium text-slate-400 mb-3">
                {selectedRadioFeed ? `Transcripts - ${selectedRadioFeed.name}` : 'Select a feed to view transcripts'}
              </h4>
              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {radioTranscripts.map((transcript) => (
                    <div key={transcript.id} className={`glass-card p-3 rounded-lg border ${
                      transcript.priority === 'urgent' ? 'border-red-500/50 bg-red-500/5' :
                      transcript.priority === 'priority' ? 'border-amber-500/50 bg-amber-500/5' : 'border-slate-800'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-slate-500">{new Date(transcript.timestamp).toLocaleTimeString()}</span>
                        <Badge className={`text-xs ${
                          transcript.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                          transcript.priority === 'priority' ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-400'
                        }`}>
                          {transcript.priority}
                        </Badge>
                      </div>
                      <p className="text-sm text-white">{transcript.transcript}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="group" className="mt-4">
          <div className="space-y-4">
            <div className="glass-card p-4 rounded-lg border border-slate-800">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4 text-teal-400" />
                <span className="text-slate-400 text-sm">Encrypted Channel - AES-256</span>
              </div>
              <div className="flex gap-2">
                <input type="text" placeholder="Type a message..." className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-teal-500" />
                <Button className="bg-teal-600 hover:bg-teal-500">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <ScrollArea className="h-[400px]">
              <div className="space-y-3">
                {groupMessages.map((msg) => (
                  <div key={msg.id} className={`glass-card p-4 rounded-lg border ${
                    msg.priority === 'urgent' ? 'border-red-500/50' :
                    msg.priority === 'priority' ? 'border-amber-500/50' : 'border-slate-800'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-teal-400">{msg.sender}</span>
                      <span className="text-xs text-slate-500">{new Date(msg.timestamp).toLocaleString()}</span>
                    </div>
                    <p className="text-white">{msg.content}</p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </TabsContent>

        <TabsContent value="deaddrops" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {deadDrops.map((drop) => (
              <div key={drop.id} className="glass-card p-4 rounded-lg border border-slate-800">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-amber-400" />
                    <span className="text-white font-medium">{drop.name}</span>
                  </div>
                  <Badge className={drop.status === 'message_waiting' ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'}>
                    {drop.status === 'message_waiting' ? `${drop.messages_pending} pending` : 'Empty'}
                  </Badge>
                </div>
                <p className="text-sm text-slate-400 mb-2">Type: {drop.type}</p>
                <p className="text-xs text-slate-500">Last check: {new Date(drop.last_check).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );

  // Render Logistics Category
  const renderLogistics = () => (
    <div className="space-y-6">
      <Tabs defaultValue={activeSubcategory || 'evacuation'} onValueChange={(v) => handleNavigate(v)}>
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="evacuation">Evacuation Routes</TabsTrigger>
          <TabsTrigger value="rally">Rally Points</TabsTrigger>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
        </TabsList>

        <TabsContent value="evacuation" className="mt-4">
          <div className="space-y-4">
            {evacuationRoutes.map((route) => (
              <div key={route.id} className={`glass-card p-4 rounded-lg border ${route.recommended ? 'border-green-500/50' : 'border-slate-800'}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Navigation className="w-4 h-4 text-teal-400" />
                      <h4 className="font-bold text-white">{route.destination?.name}</h4>
                      {route.recommended && <Badge className="bg-green-500/20 text-green-400">Recommended</Badge>}
                    </div>
                    <p className="text-sm text-slate-400 mt-1">{route.distance_miles} miles • {route.route_type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-white">{route.estimated_time_minutes} min</p>
                    <p className="text-xs text-slate-500">Safety: {Math.round(route.safety_score * 100)}%</p>
                  </div>
                </div>
                {route.hazards?.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-3">
                    <p className="text-amber-400 text-sm font-medium">⚠️ {route.hazards.length} hazard(s) on route</p>
                    {route.hazards.map((h, i) => (
                      <p key={i} className="text-xs text-slate-400 mt-1">• {h.description}</p>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-4 text-sm text-slate-400">
                  <span>Capacity: {route.destination?.capacity}</span>
                  <span>Fuel needed: {route.fuel_required_gallons} gal</span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="rally" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rallyPoints.map((point) => (
              <div key={point.id} className="glass-card p-4 rounded-lg border border-slate-800">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className={`w-4 h-4 ${
                    point.type === 'primary' ? 'text-green-400' :
                    point.type === 'secondary' ? 'text-blue-400' :
                    point.type === 'emergency' ? 'text-amber-400' : 'text-purple-400'
                  }`} />
                  <h4 className="font-bold text-white">{point.name}</h4>
                  <Badge className="bg-slate-700 text-slate-300">{point.type}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Distance</p>
                    <p className="text-white">{point.distance_from_omaha} miles</p>
                  </div>
                  <div>
                    <p className="text-slate-500">ETA</p>
                    <p className="text-white">{point.eta_minutes} min</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Capacity</p>
                    <p className="text-white">{point.capacity} people</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Assigned</p>
                    <p className="text-white">{point.members_assigned} members</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {point.amenities?.map((a, i) => (
                    <span key={i} className="text-xs px-2 py-1 rounded bg-slate-800 text-slate-300">{a}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="inventory" className="mt-4">
          {inventory && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                {Object.entries(inventory.by_category || {}).map(([cat, data]) => (
                  <div key={cat} className="glass-card p-4 rounded-lg border border-slate-800 text-center">
                    <Warehouse className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                    <p className="text-lg font-bold text-white">{data.total_items}</p>
                    <p className="text-xs text-slate-500 capitalize">{cat}</p>
                    {data.needs_attention > 0 && (
                      <Badge className="bg-amber-500/20 text-amber-400 text-xs mt-1">{data.needs_attention} alerts</Badge>
                    )}
                  </div>
                ))}
              </div>
              
              {inventory.alerts?.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <h4 className="text-amber-400 font-medium mb-2">Items Needing Attention</h4>
                  <div className="space-y-2">
                    {inventory.alerts.slice(0, 5).map((item) => (
                      <div key={item.id} className="flex items-center justify-between">
                        <span className="text-white">{item.item}</span>
                        <Badge className={item.status === 'expired' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}>
                          {item.status === 'expired' ? 'Expired' : item.status === 'expiring_soon' ? 'Expiring Soon' : 'Low Stock'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                {inventory.inventory?.slice(0, 15).map((item) => (
                  <div key={item.id} className="glass-card p-3 rounded-lg border border-slate-800 flex items-center justify-between">
                    <div>
                      <p className="text-white">{item.item}</p>
                      <p className="text-xs text-slate-500">{item.category} • {item.location}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-mono">{item.quantity} {item.unit}</p>
                      {item.expiry && <p className="text-xs text-slate-500">Exp: {item.expiry}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );

  // Render Environmental Category
  const renderEnvironmental = () => (
    <div className="space-y-6">
      <Tabs defaultValue={activeSubcategory || 'disease'} onValueChange={(v) => handleNavigate(v)}>
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="disease">Disease Tracking</TabsTrigger>
          <TabsTrigger value="weather">Weather Forecast</TabsTrigger>
          <TabsTrigger value="airquality">Air Quality</TabsTrigger>
        </TabsList>

        <TabsContent value="disease" className="mt-4">
          {diseaseData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Skull} label="Active Outbreaks" value={diseaseData.total_active} color="text-red-500" />
                <StatCard icon={Heart} label="Hospital Beds" value={diseaseData.hospital_data?.beds_available} color="text-blue-500" />
                <StatCard icon={Activity} label="ICU Capacity" value={`${Math.round((diseaseData.hospital_data?.icu_capacity || 0) * 100)}%`} color="text-amber-500" />
                <StatCard icon={Clock} label="ER Wait" value={`${diseaseData.hospital_data?.er_wait_time_minutes} min`} color="text-cyan-500" />
              </div>
              
              <div className="space-y-3">
                {diseaseData.outbreaks?.map((outbreak) => (
                  <div key={outbreak.id} className="glass-card p-4 rounded-lg border border-slate-800">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-bold text-white">{outbreak.disease}</h4>
                        <p className="text-sm text-slate-400">{outbreak.affected_area}</p>
                      </div>
                      <Badge className={`${
                        outbreak.severity === 'moderate' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {outbreak.severity}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      <span className="text-slate-400">{outbreak.cases_reported} cases</span>
                      <span className={`${
                        outbreak.trend === 'increasing' ? 'text-red-400' :
                        outbreak.trend === 'decreasing' ? 'text-green-400' : 'text-slate-400'
                      }`}>
                        {outbreak.trend === 'increasing' ? '↑' : outbreak.trend === 'decreasing' ? '↓' : '→'} {outbreak.trend}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="weather" className="mt-4">
          {weatherForecast && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
                {weatherForecast.forecast?.map((day) => (
                  <div key={day.date} className="glass-card p-4 rounded-lg border border-slate-800 text-center">
                    <p className="text-sm text-slate-400">{day.day_name.slice(0, 3)}</p>
                    <div className="my-2">
                      {day.condition === 'sunny' && <span className="text-3xl">☀️</span>}
                      {day.condition === 'partly_cloudy' && <span className="text-3xl">⛅</span>}
                      {day.condition === 'cloudy' && <span className="text-3xl">☁️</span>}
                      {day.condition === 'rain' && <span className="text-3xl">🌧️</span>}
                      {day.condition === 'thunderstorm' && <span className="text-3xl">⛈️</span>}
                      {day.condition === 'snow' && <span className="text-3xl">🌨️</span>}
                    </div>
                    <p className="text-white font-bold">{day.high_f}°</p>
                    <p className="text-slate-500 text-sm">{day.low_f}°</p>
                    {day.alerts?.length > 0 && (
                      <Badge className="bg-red-500/20 text-red-400 text-xs mt-2">Alert</Badge>
                    )}
                  </div>
                ))}
              </div>
              
              {weatherForecast.agricultural_impact && (
                <div className="glass-card p-4 rounded-lg border border-slate-800">
                  <h4 className="font-medium text-white mb-3">Agricultural Impact</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Growing Conditions</p>
                      <p className="text-white capitalize">{weatherForecast.agricultural_impact.growing_conditions}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Frost Risk</p>
                      <p className="text-white capitalize">{weatherForecast.agricultural_impact.frost_risk}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Irrigation Need</p>
                      <p className="text-white capitalize">{weatherForecast.agricultural_impact.irrigation_need}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="airquality" className="mt-4">
          {airQuality && (
            <div className="space-y-4">
              <div className="glass-card p-6 rounded-lg border border-slate-800 text-center">
                <p className="text-slate-400 mb-2">Air Quality Index</p>
                <p className={`text-5xl font-bold ${
                  airQuality.aqi <= 50 ? 'text-green-400' :
                  airQuality.aqi <= 100 ? 'text-yellow-400' :
                  airQuality.aqi <= 150 ? 'text-orange-400' : 'text-red-400'
                }`}>{airQuality.aqi}</p>
                <p className="text-lg text-white mt-2">{airQuality.category}</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="glass-card p-4 rounded-lg border border-slate-800 text-center">
                  <p className="text-xs text-slate-500">PM2.5</p>
                  <p className="text-xl font-bold text-white">{airQuality.pollutants?.pm25}</p>
                </div>
                <div className="glass-card p-4 rounded-lg border border-slate-800 text-center">
                  <p className="text-xs text-slate-500">PM10</p>
                  <p className="text-xl font-bold text-white">{airQuality.pollutants?.pm10}</p>
                </div>
                <div className="glass-card p-4 rounded-lg border border-slate-800 text-center">
                  <p className="text-xs text-slate-500">Ozone</p>
                  <p className="text-xl font-bold text-white">{airQuality.pollutants?.ozone}</p>
                </div>
                <div className="glass-card p-4 rounded-lg border border-slate-800 text-center">
                  <p className="text-xs text-slate-500">NO2</p>
                  <p className="text-xl font-bold text-white">{airQuality.pollutants?.no2}</p>
                </div>
                <div className="glass-card p-4 rounded-lg border border-slate-800 text-center">
                  <p className="text-xs text-slate-500">CO</p>
                  <p className="text-xl font-bold text-white">{airQuality.pollutants?.co}</p>
                </div>
              </div>
              
              {airQuality.hazmat_incidents?.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <h4 className="text-red-400 font-medium mb-2">⚠️ Active Hazmat Incidents</h4>
                  {airQuality.hazmat_incidents.map((incident) => (
                    <div key={incident.id} className="flex items-center justify-between mt-2">
                      <span className="text-white">{incident.type.replace('_', ' ')} - {incident.substance}</span>
                      <Badge className="bg-red-500/20 text-red-400">{incident.severity}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );

  // Render Training Category
  const renderTraining = () => (
    <div className="space-y-6">
      <Tabs defaultValue={activeSubcategory || 'scenarios'} onValueChange={(v) => handleNavigate(v)}>
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="scenarios">Training Scenarios</TabsTrigger>
          <TabsTrigger value="readiness">Readiness Score</TabsTrigger>
          <TabsTrigger value="members">Team Members</TabsTrigger>
        </TabsList>

        <TabsContent value="scenarios" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {scenarios.map((scenario) => (
              <div key={scenario.id} className="glass-card p-4 rounded-lg border border-slate-800">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-bold text-white">{scenario.title}</h4>
                    <p className="text-sm text-slate-400 capitalize">{scenario.category.replace('_', ' ')}</p>
                  </div>
                  <Badge className={`${
                    scenario.difficulty === 'hard' ? 'bg-red-500/20 text-red-400' :
                    scenario.difficulty === 'medium' ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400'
                  }`}>
                    {scenario.difficulty}
                  </Badge>
                </div>
                <p className="text-sm text-slate-300 mb-3">{scenario.description}</p>
                <div className="flex items-center justify-between text-sm text-slate-400 mb-3">
                  <span>Duration: {scenario.duration_hours}h</span>
                  <span>Team: {scenario.team_size}</span>
                </div>
                {scenario.times_completed > 0 && (
                  <p className="text-xs text-slate-500">Completed {scenario.times_completed}x • Avg score: {scenario.average_score}%</p>
                )}
                <Button className="w-full mt-3 bg-teal-600 hover:bg-teal-500">
                  <Play className="w-4 h-4 mr-2" />
                  Start Scenario
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="readiness" className="mt-4">
          {readiness && (
            <div className="space-y-4">
              <div className="glass-card p-6 rounded-lg border border-slate-800 text-center">
                <p className="text-slate-400 mb-2">Overall Readiness Score</p>
                <p className={`text-5xl font-bold ${
                  readiness.overall_score >= 80 ? 'text-green-400' :
                  readiness.overall_score >= 60 ? 'text-amber-400' : 'text-red-400'
                }`}>{readiness.overall_score}%</p>
                <p className="text-lg text-slate-400 capitalize mt-2">{readiness.overall_status.replace('_', ' ')}</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(readiness.categories || {}).map(([cat, data]) => (
                  <div key={cat} className="glass-card p-4 rounded-lg border border-slate-800">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white capitalize">{cat}</span>
                      <span className={`text-sm font-bold ${
                        data.score >= 80 ? 'text-green-400' :
                        data.score >= 60 ? 'text-amber-400' : 'text-red-400'
                      }`}>{data.score}%</span>
                    </div>
                    <Progress value={data.score} className="h-2" />
                    {data.issues?.length > 0 && (
                      <p className="text-xs text-amber-400 mt-2">{data.issues[0]}</p>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="glass-card p-4 rounded-lg border border-slate-800">
                <h4 className="font-medium text-white mb-3">Recommendations</h4>
                <ul className="space-y-2">
                  {readiness.recommendations?.map((rec, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <ClipboardCheck className="w-4 h-4 text-teal-400 mt-0.5" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="members" className="mt-4">
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <StatCard icon={Users} label="Total Members" value={members.length} color="text-blue-500" />
              <StatCard icon={Activity} label="Active" value={members.filter(m => m.status === 'active').length} color="text-green-500" />
              <StatCard icon={Clock} label="Standby" value={members.filter(m => m.status === 'standby').length} color="text-amber-500" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {members.map((member) => (
                <div key={member.id} className="glass-card p-4 rounded-lg border border-slate-800">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${member.status === 'active' ? 'bg-green-500' : 'bg-amber-500'}`} />
                      <span className="font-bold text-teal-400">{member.callsign}</span>
                    </div>
                    <Badge className="bg-slate-700 text-slate-300">{member.role}</Badge>
                  </div>
                  <p className="text-sm text-slate-400">{member.name} • {member.location}</p>
                  <p className="text-xs text-slate-500 mt-1">Last check-in: {new Date(member.last_check_in).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );

  // Main render
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-teal-500 animate-spin" />
        </div>
      );
    }

    if (!activeCategory) return renderOverview();
    if (activeCategory === 'tactical') return renderTactical();
    if (activeCategory === 'comms') return renderComms();
    if (activeCategory === 'logistics') return renderLogistics();
    if (activeCategory === 'environmental') return renderEnvironmental();
    if (activeCategory === 'training') return renderTraining();
    
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-card border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ChevronLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to Dashboard</span>
            </Link>
            <div className="h-6 w-px bg-slate-700" />
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-teal-500" />
              <h1 className="text-xl font-bold text-white">Ops Center</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => {
                setLoading(true);
                setTimeout(() => setLoading(false), 1000);
              }}
              className="text-slate-400 hover:text-white"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Badge className="bg-green-500/20 text-green-400">
              <div className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse" />
              System Online
            </Badge>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Breadcrumb items={breadcrumbItems} onNavigate={handleNavigate} />
        {renderContent()}
      </main>
    </div>
  );
};

export default OpsCenter;
